import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// BOM Hooks
export function useBOMs(options: {
  finished_item_id?: string;
  bom_type?: string;
  active_only?: boolean;
  page?: number;
  page_size?: number;
} = {}) {
  return useQuery({
    queryKey: ['boms', options],
    queryFn: () => api.getBOMs(options),
    staleTime: 5 * 60 * 1000,
  });
}

export function useBOM(bomId: string) {
  return useQuery({
    queryKey: ['bom', bomId],
    queryFn: () => api.getBOM(bomId),
    enabled: !!bomId,
  });
}

export function useBOMComponents(bomId: string) {
  return useQuery({
    queryKey: ['bom-components', bomId],
    queryFn: () => api.getBOMComponents(bomId),
    enabled: !!bomId,
  });
}

export function useBOMCostBreakdown(bomId: string) {
  return useQuery({
    queryKey: ['bom-cost-breakdown', bomId],
    queryFn: () => api.getBOMCostBreakdown(bomId),
    enabled: !!bomId,
  });
}

export function useCreateBOM() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createBOM(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boms'] });
    },
  });
}

export function useUpdateBOM() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      api.updateBOM(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boms'] });
      queryClient.invalidateQueries({ queryKey: ['bom'] });
    },
  });
}

// Production Order Hooks
export function useProductionOrders(options: {
  status?: string;
  bom_id?: string;
  page?: number;
  page_size?: number;
} = {}) {
  return useQuery({
    queryKey: ['production-orders', options],
    queryFn: () => api.getProductionOrders(options),
    staleTime: 5 * 60 * 1000,
  });
}

export function useProductionOrder(productionId: string) {
  return useQuery({
    queryKey: ['production-order', productionId],
    queryFn: () => api.getProductionOrder(productionId),
    enabled: !!productionId,
  });
}

export function useProductionMaterials(productionId: string) {
  return useQuery({
    queryKey: ['production-materials', productionId],
    queryFn: () => api.getProductionMaterials(productionId),
    enabled: !!productionId,
  });
}

export function useCreateProductionOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createProductionOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
    },
  });
}

export function useStartProduction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productionId: string) => api.startProduction(productionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
    },
  });
}

export function useCompleteProduction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productionId, quantityProduced, quantityScrapped }: { 
      productionId: string;
      quantityProduced: number;
      quantityScrapped?: number;
    }) => api.completeProduction(productionId, quantityProduced, quantityScrapped),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-materials'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useIssueMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productionId, itemId, quantity, batchId, notes }: { 
      productionId: string;
      itemId: string;
      quantity: number;
      batchId?: string;
      notes?: string;
    }) => api.issueMaterial(productionId, itemId, quantity, batchId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-materials'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}
